import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase-auth';
import { 
  DevicePhoneMobileIcon, 
  ComputerDesktopIcon, 
  TrashIcon, 
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

interface TrustedDevice {
  id: string;
  device_name: string;
  device_hash: string;
  device_metadata: {
    browser?: string;
    os?: string;
    platform?: string;
    userAgent?: string;
  };
  last_seen: string;
  created_at: string;
  expires_at: string;
  ip_address?: string;
  is_active: boolean;
  revoked_at?: string;
  revoked_reason?: string;
}

export default function DeviceManagement() {
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('trusted_devices')
        .select('*')
        .eq('user_id', user.id)
        .order('last_seen', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const revokeDevice = async (deviceId: string) => {
    setRevoking(deviceId);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update device status
      const { error } = await supabase
        .from('trusted_devices')
        .update({
          is_active: false,
          revoked_at: new Date().toISOString(),
          revoked_reason: 'user_revoked'
        })
        .eq('id', deviceId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Log the revocation
      await supabase
        .from('device_events')
        .insert({
          user_id: user.id,
          device_id: deviceId,
          event_type: 'device_revoked',
          metadata: { reason: 'user_action' }
        });

      // Refresh the list
      await fetchDevices();
      setShowRevokeConfirm(null);
    } catch (err) {
      console.error('Error revoking device:', err);
      setError('Failed to revoke device');
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (device: TrustedDevice) => {
    const platform = device.device_metadata?.platform?.toLowerCase() || '';
    const userAgent = device.device_metadata?.userAgent?.toLowerCase() || '';

    if (platform.includes('mobile') || userAgent.includes('mobile')) {
      return <DevicePhoneMobileIcon className="h-6 w-6" />;
    }
    return <ComputerDesktopIcon className="h-6 w-6" />;
  };

  const getDeviceStatus = (device: TrustedDevice) => {
    if (!device.is_active) {
      return { text: 'Revoked', color: 'text-red-600', bgColor: 'bg-red-50' };
    }

    const expiresAt = new Date(device.expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 0) {
      return { text: 'Expired', color: 'text-gray-600', bgColor: 'bg-gray-50' };
    } else if (daysUntilExpiry <= 30) {
      return { text: `Expires in ${daysUntilExpiry}d`, color: 'text-orange-600', bgColor: 'bg-orange-50' };
    } else {
      return { text: 'Active', color: 'text-green-600', bgColor: 'bg-green-50' };
    }
  };

  const getCurrentDevice = async () => {
    // This would compare the current device hash with stored devices
    // For now, we'll just highlight the most recently used device
    return devices.find(d => d.is_active)?.id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  const activeDevices = devices.filter(d => d.is_active);
  const inactiveDevices = devices.filter(d => !d.is_active);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white portal-content-shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Trusted Devices</h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage devices that can access your account without 2FA verification
              </p>
            </div>
            <ShieldCheckIcon className="h-8 w-8 text-amber-500" />
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <strong>Security Tip:</strong> Only trust devices you personally own and control. 
                Remove any devices you don't recognize immediately.
              </p>
            </div>
          </div>
        </div>

        {/* Active Devices */}
        <div className="px-6 py-4">
          <h4 className="text-sm font-medium text-gray-900 mb-4">
            Active Devices ({activeDevices.length})
          </h4>
          
          {activeDevices.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No trusted devices. You'll need to verify with 2FA on each login.
            </p>
          ) : (
            <div className="space-y-4">
              {activeDevices.map((device) => {
                const status = getDeviceStatus(device);
                return (
                  <div
                    key={device.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          {getDeviceIcon(device)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h5 className="text-sm font-medium text-gray-900">
                              {device.device_name}
                            </h5>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                              {status.text}
                            </span>
                          </div>
                          
                          <div className="mt-1 text-xs text-gray-500 space-y-1">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <ClockIcon className="h-3 w-3" />
                                Last used {formatDistanceToNow(new Date(device.last_seen), { addSuffix: true })}
                              </span>
                              {device.ip_address && (
                                <span className="flex items-center gap-1">
                                  <MapPinIcon className="h-3 w-3" />
                                  {device.ip_address}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span>Added {formatDistanceToNow(new Date(device.created_at), { addSuffix: true })}</span>
                              {device.device_metadata?.hardwareConcurrency && (
                                <span className="text-gray-400">• {device.device_metadata.hardwareConcurrency} CPU cores</span>
                              )}
                              {device.device_metadata?.deviceMemory && (
                                <span className="text-gray-400">• {device.device_metadata.deviceMemory}GB RAM</span>
                              )}
                              {device.device_metadata?.touchSupport && (
                                <span className="text-gray-400">• Touch</span>
                              )}
                            </div>
                            {device.device_metadata?.webglRenderer && (
                              <div className="text-xs text-gray-400 truncate" title={device.device_metadata.webglRenderer}>
                                GPU: {device.device_metadata.webglRenderer}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center ml-4">
                        {showRevokeConfirm === device.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Remove?</span>
                            <button
                              onClick={() => revokeDevice(device.id)}
                              disabled={revoking === device.id}
                              className="text-red-600 hover:text-red-700 text-sm font-medium"
                            >
                              {revoking === device.id ? 'Removing...' : 'Yes'}
                            </button>
                            <button
                              onClick={() => setShowRevokeConfirm(null)}
                              className="text-gray-600 hover:text-gray-700 text-sm font-medium"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowRevokeConfirm(device.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Inactive Devices */}
        {inactiveDevices.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              Inactive Devices ({inactiveDevices.length})
            </h4>
            <div className="space-y-3">
              {inactiveDevices.map((device) => (
                <div
                  key={device.id}
                  className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 opacity-50">
                        {getDeviceIcon(device)}
                      </div>
                      <div>
                        <h5 className="text-sm text-gray-600">
                          {device.device_name}
                        </h5>
                        <p className="text-xs text-gray-500">
                          {device.revoked_reason === 'expired' ? 'Expired' : 'Revoked'} {formatDistanceToNow(new Date(device.revoked_at!), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <p className="text-xs text-gray-500">
            Trusted devices can skip 2FA for up to 180 days. You can have a maximum of 5 active devices.
          </p>
        </div>
      </div>
    </div>
  );
}